import React, { useCallback, useEffect, useState } from "react";

import Dropdown from "antd/es/dropdown";
import Menu from "antd/es/menu";
import { defineMessage, defineMessages, useIntl } from "react-intl";

import FilterOperator from "@mapmycustomers/shared/enum/FilterOperator";
import FilterModel from "@mapmycustomers/shared/types/viewModel/internalModel/FilterModel";

import RoundButton from "@app/component/RoundButton";
import useAnalytics from "@app/util/contexts/useAnalytics";

const CLEAR_KEY = -1;
const CUSTOM_KEY = -2;

enum CadenceStatus {
  IS_PAST_DUE,
  WILL_BE_OVERDUE,
  IS_UNKNOWN,
}

const FILTER_VALUES = [
  {
    operator: FilterOperator.IS_OVERDUE,
    value: { operator: FilterOperator.IS_OVERDUE, value: undefined },
  },
  {
    operator: FilterOperator.WILL_BE_OVERDUE,
    value: {
      operator: FilterOperator.GREATER_THAN,
      value: 0,
    },
  },
  { operator: FilterOperator.EMPTY, value: undefined },
];

const messages = defineMessages({
  [CadenceStatus.IS_PAST_DUE]: {
    id: "component.quickFilters.frequencyStatus.pastDue",
    defaultMessage: "is past due",
    description: "is past due option label in frequency Status filter in Quick filters",
  },
  [CadenceStatus.IS_UNKNOWN]: {
    id: "component.quickFilters.frequencyStatus.isUnknown",
    defaultMessage: "is unknown",
    description: "Is unknown option label in frequency Status filter in Quick filters",
  },
  [CadenceStatus.WILL_BE_OVERDUE]: {
    id: "component.quickFilters.frequencyStatus.notYetPastDue",
    defaultMessage: "is not yet past due",
    description: "is not yet past due option label in frequency Status filter in Quick filters",
  },
});

const customLabelMessage = defineMessage({
  id: "component.quickFilters.cadenceStatus.custom",
  defaultMessage: "Custom",
  description: "Custom option in dropdown in Cadence Status quick filter",
});

interface Props {
  fieldName?: string;
  filter: FilterModel;
  onSetCustomFilters: (customFilters: Partial<FilterModel>) => void;
  onUpdateFilterModel: (filterModel: Partial<FilterModel>) => void;
}

const FrequencyStatusFilter: React.FC<Props> = ({
  fieldName = "cadenceDaysOut",
  filter,
  onSetCustomFilters,
  onUpdateFilterModel,
}) => {
  const analytics = useAnalytics();
  const intl = useIntl();
  const [status, setStatus] = useState<CadenceStatus | undefined>(undefined);

  const handleMenuClick = useCallback(
    ({ key }) => {
      const value = parseInt(key, 10);
      if (value === CUSTOM_KEY) {
        onSetCustomFilters({
          [fieldName]: { operator: FilterOperator.WILL_BE_OVERDUE, value: 0 },
        });
        analytics.clicked(["Cadence Status", "Custom"]);
        return;
      }
      onUpdateFilterModel({ [fieldName]: value === CLEAR_KEY ? undefined : FILTER_VALUES[value] });
      const { operator } = FILTER_VALUES[value];
      switch (operator) {
        case FilterOperator.EMPTY:
          analytics.clicked(["Frequency Status", "unknown"]);
          break;
        case FilterOperator.IS_OVERDUE:
          analytics.clicked(["Frequency Status", "is overdue"]);
          break;
        case FilterOperator.IS_UPCOMING:
          analytics.clicked(["Frequency Status", "is not overdue"]);
          break;
      }
    },
    [analytics, fieldName, onSetCustomFilters, onUpdateFilterModel]
  );

  useEffect(() => {
    if (filter && fieldName in filter) {
      const filterCondition = filter[fieldName];
      // we don't compare values, because all of them are undefined, but if you add new
      // filter values, make sure to add another condition by value
      const valueIndex = FILTER_VALUES.findIndex(
        (value) => filterCondition.operator === value.operator
      );
      setStatus(valueIndex < 0 ? undefined : valueIndex);
    } else {
      setStatus(undefined);
    }
  }, [fieldName, filter]);

  return (
    <Dropdown
      overlay={
        <Menu onClick={handleMenuClick}>
          {status !== undefined && (
            <Menu.Item key={CLEAR_KEY}>
              <b>
                {intl.formatMessage({
                  id: "component.quickFilters.frequencyStatus.clear",
                  defaultMessage: "Clear filter",
                  description: "Clear option in dropdown in frequency Status Quick filter",
                })}
              </b>
            </Menu.Item>
          )}
          {FILTER_VALUES.map((value, index) => (
            <Menu.Item key={index}>
              {intl.formatMessage(messages[index as CadenceStatus])}
            </Menu.Item>
          ))}
          <Menu.Item key={CUSTOM_KEY}>{intl.formatMessage(customLabelMessage)}</Menu.Item>
        </Menu>
      }
      trigger={["click"]}
    >
      <RoundButton active={status !== undefined} hasMenu>
        {intl.formatMessage(
          {
            id: "component.quickFilters.frequencyStatus.title",
            defaultMessage: "Frequency Status{isSet, select, true {: {valueName}} other {}}",
            description: "Frequency Status quick filter button's title",
          },
          {
            isSet: status !== undefined,
            valueName: status !== undefined ? intl.formatMessage(messages[status]) : "",
          }
        )}
      </RoundButton>
    </Dropdown>
  );
};

export default FrequencyStatusFilter;
